// src/components/InfluencerPage.js

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useData } from '../context/DataContext';
import { useLocation } from 'react-router-dom';
import './InfluencerPage.css';
import PluginSelector from './PluginSelector';
import Spinner from './Spinner';
import ErrorDisplayModal from './ErrorDisplayModal';
import { toast } from 'react-toastify';
import ConfirmationModal from './../utils/ConfirmationModal';
import './ErrorDisplayModal.module.css';
import HealthDashboardModal from './HealthDashboardModal'; // NEW: Import the modal
import ContentFocusDashboardModal from './ContentFocusDashboardModal'; // NEW
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; // NEW
import { faSync } from '@fortawesome/free-solid-svg-icons'; // NEW
import { countryList } from './../utils/CountryList';
import { languageList } from './../utils/LanguageList';
import { MENU_STATUS } from './../config/MenuInfluencerStatus';
import {
  validateHeaderInfo,
  validateChannelInputs,
  validatePartnerInfo,
} from './../utils/ValidationUtils'; //

// Helper function to format links for href attributes
const formatUrlForHref = (url) => {
  if (
    !url ||
    typeof url !== 'string' ||
    url.trim() === '' ||
    url.trim() === '#'
  ) {
    return '#';
  }
  const trimmedUrl = url.trim();
  // Check if it already has a common protocol or is a protocol-relative URL
  if (/^(https?:\/\/|mailto:|tel:|\/\/)/i.test(trimmedUrl)) {
    return trimmedUrl;
  }
  // If not, prepend "https://"
  return `https://${trimmedUrl}`;
};

// Helper function to format dates for display and editing
const formatDateForInput = (dateString) => {
  if (!dateString) return ''; // Handles null, undefined, ''
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      // Checks for invalid date results
      return '';
    }
    return date.toISOString().slice(0, 10);
  } catch (e) {
    return '';
  }
};

const getTierClassName = (tier) => {
  switch (tier?.toLowerCase()) {
    // Health & Quality Tiers
    case 'prime':
    case 'excellent': // 'Excellent' now correctly maps to 'prime' (green)
      return 'prime';
    case 'promising':
    case 'great': // 'Great' now correctly maps to 'promising' (blue)
    case 'good':
      return 'promising';
    case 'acceptable':
    case 'moderate':
      return 'acceptable';
    case 'mid-risk': // 'Mid-Risk' tier might not have a unique style, mapping to acceptable
    case 'low':
      return 'midRisk';
    case 'high-risk':
    case 'poor':
      return 'highRisk';
    // Common Tiers
    case 'incomplete':
    case 'insufficient data':
    case 'competitor':
      return 'insufficient';
    case 'inactive':
      return 'inactive';
    case 'error':
      return 'error';
    default:
      return 'unknown';
  }
};

const InfluencerPage = () => {
  const {
    queryResults,
    isLoadingQueryData,
    activeinfluencerId,
    setActiveinfluencerId,
    fetchInfluencerPageData,
    fetchInfluencerData,
    fetchSocialMedias,
    updateProducts,
    setError,
    error,
    clearError,
    fetchSocialContacts,
    addSocialContact,
    addSocialMedia,
    updateSocialContact,
    updateSocialMedia,
    updatePartnerInfo,
    lastQuery,
    runSingleQuery,
    runMultipleQuery,
    setQueryResults,
    setQueryMode,
    addNewInfluencer,
    getInfluencerDetails,
    deleteInfluencer,
    fetchInfluencerHealthDashboard,
    refreshInfluencerHealth,
    refreshInfluencerContentFocus,
    fetchInfluencerContentFocusDashboard,
  } = useData();

  const location = useLocation(); // Get location

  const [influencerData, setInfluencerData] = useState([]);
  const [productUpdates, setProductUpdates] = useState([]);
  const [productsData, setProductsData] = useState([]);
  const [isAddingNewInfluencer, setIsAddingNewInfluencer] = useState(false);

  const [socialMedias, setSocialMedias] = useState([]);
  const [selectedSocialMedia, setSelectedSocialMedia] = useState(null);

  // State for social contact channels
  const [influencerContactChannelsData, setInfluencerContactChannelsData] =
    useState([]); // Stores the full fetched contact objects
  const [selectedContactChannel, setSelectedContactChannel] = useState(null); // Stores the selected *contact object*

  const [isLoadingPageData, setIsLoadingPageData] = useState(false);
  const [isSavingProduct, setIsSavingProduct] = useState(false);
  const [spinnerMessage, setSpinnerMessage] = useState('');

  // --- NEW STATES FOR ADD/EDIT MODE ---
  const [isAddMode, setIsAddMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false); // New state for edit mode
  const [isSavingChannel, setIsSavingChannel] = useState(false);
  const [newContactInfo, setNewContactInfo] = useState({
    type: 'gmail',
    identifier: '',
    link: '',
  });
  const [newPublishingInfo, setNewPublishingInfo] = useState({
    type: 'youtube',
    link: '',
    subscribers: '',
    viewCount: '',
  });
  const [editContactForm, setEditContactForm] = useState({
    id: null,
    identifier: '',
    link: '',
  });
  const [editPublishingForm, setEditPublishingForm] = useState({
    id: null,
    link: '',
    subscribers: '',
    viewCount: '',
  });
  const [validationErrors, setValidationErrors] = useState({});

  // --- NEW STATES FOR PARTNER INFO EDIT MODE ---
  const [isPartnerEditMode, setIsPartnerEditMode] = useState(false);
  const [isSavingPartnerInfo, setIsSavingPartnerInfo] = useState(false);
  const [partnerInfoForm, setPartnerInfoForm] = useState({});
  const [partnerValidationErrors, setPartnerValidationErrors] = useState({});

  // --- NEW STATES FOR HEADER EDIT MODE ---
  const [isHeaderEditMode, setIsHeaderEditMode] = useState(false);
  const [isSavingHeaderInfo, setIsSavingHeaderInfo] = useState(false);
  const [headerInfoForm, setHeaderInfoForm] = useState({});
  const [headerValidationErrors, setHeaderValidationErrors] = useState({});

  // --- NEW STATES for delete confirmation ---
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // --- NEW STATES FOR HEALTH DASHBOARD ---
  const [isHealthModalOpen, setIsHealthModalOpen] = useState(false);
  const [healthDashboardData, setHealthDashboardData] = useState(null);
  const [isHealthDashboardLoading, setIsHealthDashboardLoading] =
    useState(false);
  const [isRefreshingHealth, setIsRefreshingHealth] = useState(false);

  // --- NEW STATES FOR CONTENT FOCUS DASHBOARD ---
  const [isContentFocusModalOpen, setIsContentFocusModalOpen] = useState(false);
  const [contentFocusDashboardData, setContentFocusDashboardData] =
    useState(null);
  const [isContentFocusDashboardLoading, setIsContentFocusDashboardLoading] =
    useState(false);
  const [isRefreshingContentFocus, setIsRefreshingContentFocus] =
    useState(false);

  // --- 1. Deriving state from context ---

  // We create a "Merged" influencer object.
  // Priority 1: Global Context (if loaded and complete)
  // Priority 2: Location State (passed from Modal)
  const activeInfluencer = useMemo(() => {
    // Try to find in global store
    const fromContext = (queryResults || []).find(
      (item) => item.influencerId === activeinfluencerId,
    );

    // If found in context, return it.
    // BUT, if we have an override channel from navigation, inject it here to prevent UI flicker
    if (fromContext) {
      if (location.state?.activeChannelOverride) {
        return {
          ...fromContext,
          activeInfluencerChannel: location.state.activeChannelOverride,
        };
      }
      return fromContext;
    }

    // If NOT in context (e.g. first load, missing from list), construct a "Stub" from navigation state
    if (location.state?.influencerId === activeinfluencerId) {
      return {
        influencerId: activeinfluencerId,
        name: location.state.influencerName || `ID: ${activeinfluencerId}`,
        activeInfluencerChannel: location.state.activeChannelOverride || null,
        status: 'LOADING...', // Placeholder status while fetch happens
        country: 'Loading...',
        idiom: 'Loading...',
        // Add other safe defaults to prevent crashes
      };
    }

    return null;
  }, [queryResults, activeinfluencerId, location.state]);

  const activeChannel = useMemo(() => {
    if (!activeinfluencerId || !Array.isArray(queryResults)) return null;
    return queryResults.find(
      (item) => item.influencerId === activeinfluencerId,
    );
  }, [queryResults, activeinfluencerId]);

  const influencerInfo = useMemo(() => {
    return Array.isArray(influencerData)
      ? influencerData.find(
          (data) => data.influencerId === activeinfluencerId,
        ) || {}
      : {};
  }, [influencerData, activeinfluencerId]);

  const historicAvgViews = useMemo(() => {
    const views = selectedSocialMedia?.viewCount;
    const videos = selectedSocialMedia?.videoCount;

    // Gracefully handle cases where data is missing or would cause a division by zero.
    if (!views || !videos || videos === 0) {
      return 'N/A';
    }
    return Math.round(views / videos).toLocaleString();
  }, [selectedSocialMedia]);

  const recentAvgViews = useMemo(() => {
    // ENHANCEMENT: avgRecentViews now comes directly from the selectedSocialMedia object,
    // which was populated by our new high-performance API endpoint.
    const avgViews = selectedSocialMedia?.avgRecentViews;

    if (avgViews === null || avgViews === undefined) {
      return 'N/A';
    }
    return Math.round(avgViews).toLocaleString();
  }, [selectedSocialMedia]);

  // --- INITIAL DATA FETCHING EFFECT ---
  useEffect(() => {
    // This effect now has a single responsibility: load all data for the active influencer.
    if (activeinfluencerId) {
      setSocialMedias([]);
      setSelectedSocialMedia(null);
      setInfluencerContactChannelsData([]);
      setSelectedContactChannel(null);
      setIsLoadingPageData(true);
      setSpinnerMessage(
        `Loading data for ${activeChannel?.nickname || 'the influencer'}...`,
      );

      // A SINGLE, ROBUST API CALL
      fetchInfluencerPageData(activeinfluencerId)
        .then((result) => {
          if (result.status) {
            const { influencerInfo, products, socialMedias, socialContacts } =
              result.data;

            // Update all relevant states from the single payload
            setInfluencerData(influencerInfo ? [influencerInfo] : []);
            setProductsData(products || []);
            setProductUpdates(products || []);
            setSocialMedias(socialMedias || []);
            setInfluencerContactChannelsData(socialContacts || []);

            // Set the default selected channels after data is loaded
            const activeContact =
              socialContacts?.find((c) => c.activeContact === 1) ||
              socialContacts?.[0] ||
              null;
            setSelectedContactChannel(activeContact);
          } else {
            // Clear all data on failure
            setInfluencerData([]);
            setProductsData([]);
            setProductUpdates([]);
            setSocialMedias([]);
            setInfluencerContactChannelsData([]);
          }
        })
        .catch((err) => {
          // This catches unexpected client-side errors
          console.error('Critical error fetching page data:', err);
        })
        .finally(() => {
          setIsLoadingPageData(false);
          setSpinnerMessage('');
        });
    } else if (!activeinfluencerId) {
      // Clear all data if no influencer is active (this part is unchanged and correct)
      setInfluencerData([]);
      setProductsData([]);
      setProductUpdates([]);
      setSocialMedias([]);
      setSelectedSocialMedia(null);
      setInfluencerContactChannelsData([]);
      setSelectedContactChannel(null);
    }
    // The dependency array is now simpler
  }, [activeinfluencerId, fetchInfluencerPageData, setError]);

  // --- EFFECT TO SYNC SELECTED CHANNEL WITH THE AVAILABLE LIST ---
  // Re-factored so it doesn't wipe out selected elements on array refreshes
  // and properly preserves the selection containing health parameters.
  useEffect(() => {
    if (socialMedias && socialMedias.length > 0) {
      setSelectedSocialMedia((prevSelected) => {
        if (prevSelected) {
          const updatedSelection = socialMedias.find(
            (sm) => sm.id === prevSelected.id,
          );
          if (updatedSelection) return updatedSelection;
        }
        return socialMedias[0];
      });
    } else {
      setSelectedSocialMedia(null);
    }
  }, [socialMedias]);

  // --- EFFECT TO POPULATE EDIT FORMS ---
  // This effect runs when edit mode is toggled or the selected channel changes,
  // ensuring the form always shows the correct data for editing.
  useEffect(() => {
    if (isEditMode) {
      setEditContactForm({
        id: selectedContactChannel?.id ?? null,
        identifier: selectedContactChannel?.accountIdentifier ?? '',
        link: selectedContactChannel?.socialMediaLink ?? '',
      });
      setEditPublishingForm({
        id: selectedSocialMedia?.id ?? null,
        link: selectedSocialMedia?.socialMediaLink ?? '',
        subscribers: selectedSocialMedia?.subscribers ?? '',
        viewCount: selectedSocialMedia?.viewCount ?? '',
      });
    }
  }, [isEditMode, selectedContactChannel, selectedSocialMedia]);

  // EFFECT to sync partner info form when influencerInfo changes or edit mode is toggled.
  // REFACTORED: Uses nullish coalescing (??) to ensure that only null/undefined values
  // are replaced with an empty string, preserving valid values like 0 or ''.
  useEffect(() => {
    if (influencerInfo.influencerId) {
      setPartnerInfoForm({
        promoCode: influencerInfo.promoCode ?? '',
        emailPromoCode: influencerInfo.emailPromoCode ?? '',
        affiliateLink: influencerInfo.affiliateLink ?? '',
        affiliateLink2: influencerInfo.affiliateLink2 ?? '',
        publicationPrice: influencerInfo.publicationPrice ?? '',
        commissionPercent:
          influencerInfo.commissionPercent !== null &&
          influencerInfo.commissionPercent !== undefined
            ? influencerInfo.commissionPercent * 100
            : '', // Explicit check for number conversion
        dateOfPayment: formatDateForInput(influencerInfo.dateOfPayment),
        revenueUSD: influencerInfo.revenueUSD ?? '',
      });
    }
  }, [influencerInfo, isPartnerEditMode]); // Re-sync form data when influencer or mode changes

  // EFFECT to sync header form when activeChannel changes or header edit mode is toggled.
  // REFACTORED: Uses nullish coalescing (??) for robustness and consistency.
  useEffect(() => {
    if (activeChannel) {
      setHeaderInfoForm({
        name: activeChannel.name ?? '',
        status: activeChannel.status ?? '',
        nickname: activeChannel.nickname ?? '',
        country: activeChannel.country ?? '',
        idiom: activeChannel.idiom ?? '',
        dateLastContact: formatDateForInput(activeChannel.dateLastContact),
      });
    }
  }, [activeChannel, isHeaderEditMode]); // Re-sync form data when influencer or mode changes

  // Effect to handle state resets when the influencer changes
  useEffect(() => {
    // If any edit/add modes are active, turn them off when the user navigates
    if (isAddMode) setIsAddMode(false);
    if (isEditMode) setIsEditMode(false);
    if (isPartnerEditMode) setIsPartnerEditMode(false);
    if (isHeaderEditMode) setIsHeaderEditMode(false);
  }, [activeinfluencerId]);

  // --- DATA FETCHING EFFECT ---
  const refetchChannels = useCallback(async () => {
    if (!activeinfluencerId) return;

    // This function will now handle its own state setting.
    // It's designed to be called after a save or during the initial load.
    try {
      const [socialMediasResult, socialContactsResult] = await Promise.all([
        fetchSocialMedias(activeinfluencerId),
        fetchSocialContacts(activeinfluencerId),
      ]);

      // --- CORRECTED: Handle Social Medias Result ---
      if (socialMediasResult?.status && socialMediasResult.data) {
        setSocialMedias(socialMediasResult.data);
        // Only set a default selected media if we are NOT in add mode,
        // otherwise, let the existing selection persist until the user saves/cancels.
        setSelectedSocialMedia(socialMediasResult.data[0] || null);
      } else {
        // THIS IS THE CRUCIAL PART: Reset state if the fetch was not successful.
        setSocialMedias([]);
        setSelectedSocialMedia(null);
        console.warn(
          'Failed to fetch social media records:',
          socialMediasResult?.message,
        );
      }

      // --- CORRECTED: Handle Social Contacts Result ---
      if (socialContactsResult?.status && socialContactsResult.data) {
        const fetchedContacts = socialContactsResult.data;
        setInfluencerContactChannelsData(fetchedContacts);
        const activeContact =
          fetchedContacts.find((c) => c.activeContact === 1) ||
          fetchedContacts[0] ||
          null;
        setSelectedContactChannel(activeContact);
      } else {
        // THIS IS THE CRUCIAL PART: Reset state if the fetch was not successful.
        setInfluencerContactChannelsData([]);
        setSelectedContactChannel(null);
        console.warn(
          'Failed to fetch social contact channels:',
          socialContactsResult?.message,
        );
      }
    } catch (err) {
      console.error(
        'A network or unexpected error occurred while refetching channels:',
        err,
      );
      // On a catastrophic failure, clear everything and show an error.
      setSocialMedias([]);
      setSelectedSocialMedia(null);
      setInfluencerContactChannelsData([]);
      setSelectedContactChannel(null);
      setError('Could not refresh channel information due to a network error.');
    }
  }, [
    activeinfluencerId,
    fetchSocialMedias,
    fetchSocialContacts,
    setError,
    setSocialMedias,
    setSelectedSocialMedia,
    setInfluencerContactChannelsData,
    setSelectedContactChannel,
  ]);

  const handlePrevious = useCallback(() => {
    const currentIndex = queryResults.findIndex(
      (item) => item.influencerId === activeinfluencerId,
    );
    if (currentIndex > 0) {
      setActiveinfluencerId(queryResults[currentIndex - 1].influencerId);
    }
  }, [queryResults, activeinfluencerId, setActiveinfluencerId]);

  const handleNext = useCallback(() => {
    const currentIndex = queryResults.findIndex(
      (item) => item.influencerId === activeinfluencerId,
    );
    if (currentIndex < queryResults.length - 1) {
      setActiveinfluencerId(queryResults[currentIndex + 1].influencerId);
    }
  }, [queryResults, activeinfluencerId, setActiveinfluencerId]);

  const handlePluginToggle = useCallback(
    (pluginId) => {
      if (!productsData) return;
      const updatedPlugins = productsData.map((plugin) => {
        if (plugin.id === pluginId) {
          return { ...plugin, isSelected: !plugin.isSelected };
        }
        return plugin;
      });
      setProductsData(updatedPlugins);
      setProductUpdates(updatedPlugins);
    },
    [productsData],
  );

  const handleSaveProducts = useCallback(async () => {
    setIsSavingProduct(true);
    setSpinnerMessage(
      `Please wait while updating used plug-ins for ${activeChannel?.nickname || 'the influencer'}...`,
    );
    try {
      const productsArray = productUpdates.map((product) => ({
        id: product.id,
        isSelected: product.isSelected,
        productName: product.productName,
        influencerId: activeinfluencerId,
      }));
      await updateProducts(activeinfluencerId, productsArray);
    } catch (err) {
      console.error('Error updating products:', err);
      setError(
        err.response?.data?.message ||
          err.message ||
          'Error occurred while updating products.',
      );
    } finally {
      setIsSavingProduct(false);
      setSpinnerMessage('');
    }
  }, [
    activeinfluencerId,
    activeChannel?.nickname,
    productUpdates,
    updateProducts,
    setError,
  ]);

  // CORRECTED: Handle change for the contact channel dropdown
  // The value from the event will be the 'id' of the contact channel record
  const handleContactChannelChange = useCallback(
    (event) => {
      const selectedId = parseInt(event.target.value, 10); // Assuming 'id' is an integer
      const selected = influencerContactChannelsData.find(
        (contact) => contact.id === selectedId,
      );
      setSelectedContactChannel(selected || null);
    },
    [influencerContactChannelsData],
  );

  // --- NEW HANDLERS FOR ADD/EDIT/CANCEL MODE  ---
  const toggleAddMode = useCallback(() => {
    setIsEditMode(false); // Ensure edit mode is off
    setIsAddMode((prev) => !prev);
    setValidationErrors({});
    if (!isAddMode) {
      // Entering add mode
      setNewContactInfo({ type: 'gmail', identifier: '', link: '' });
      setNewPublishingInfo({
        type: 'youtube',
        link: '',
        subscribers: '',
        viewCount: '',
      });
    }
  }, [isAddMode]);

  const toggleEditMode = useCallback(() => {
    setIsAddMode(false); // Ensure add mode is off
    setIsEditMode((prev) => !prev);
    setValidationErrors({});
  }, []);

  const handleCancel = () => {
    setIsAddMode(false);
    setIsEditMode(false);
    setValidationErrors({});
  };

  const handleFormChange = (e, formType) => {
    const { name, value } = e.target;
    const formSetter = {
      newContact: setNewContactInfo,
      newPublishing: setNewPublishingInfo,
      editContact: setEditContactForm,
      editPublishing: setEditPublishingForm,
    }[formType];

    if (formSetter) {
      formSetter((prev) => ({ ...prev, [name]: value }));
    }

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleNewChannelChange = (e, channelType) => {
    const { name, value } = e.target;
    if (channelType === 'contact') {
      setNewContactInfo((prev) => ({ ...prev, [name]: value }));
    } else {
      setNewPublishingInfo((prev) => ({ ...prev, [name]: value }));
    }
    // Clear validation error for the field being typed in
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSaveChanges = useCallback(async () => {
    setIsSavingChannel(true);
    setSpinnerMessage('Saving channel information...');

    // 1. Determine which validation to run and execute it.
    let errors = {};
    if (isAddMode) {
      errors = validateChannelInputs(
        newContactInfo,
        newPublishingInfo,
        influencerContactChannelsData,
        socialMedias,
      );
    } else if (isEditMode) {
      // Use a non-conflicting list for duplicate checks during format validation.
      const otherContacts = influencerContactChannelsData.filter(
        (c) => c.id !== editContactForm.id,
      );
      const otherPublishing = socialMedias.filter(
        (p) => p.id !== editPublishingForm.id,
      );
      errors = validateChannelInputs(
        { type: selectedContactChannel?.socialMedia, ...editContactForm },
        { type: selectedSocialMedia?.socialMedia, ...editPublishingForm },
        otherContacts,
        otherPublishing,
      );
    }

    // 2. Set the state with the validation results. This is the missing step.
    setValidationErrors(errors);

    // 3. Check for errors and show a user-friendly global message, then stop.
    if (Object.keys(errors).length > 0) {
      setError('Please fix the validation errors before saving.');
      setIsSavingChannel(false); // Stop the loading spinner
      return; // Exit the function
    }

    try {
      let promises = [];

      // Logic for ADDING new channels
      if (isAddMode) {
        const addErrors = validateChannelInputs(
          newContactInfo,
          newPublishingInfo,
          influencerContactChannelsData,
          socialMedias,
        );
        if (Object.keys(addErrors).length > 0)
          throw new Error('Please fix the validation errors.');

        if (newContactInfo.identifier) {
          promises.push(
            addSocialContact({
              influencerId: activeinfluencerId,
              socialMedia: newContactInfo.type,
              accountIdentifier: newContactInfo.identifier,
              socialMediaLink: newContactInfo.link || null,
            }),
          );
        }
        if (newPublishingInfo.link) {
          promises.push(
            addSocialMedia({
              influencerId: activeinfluencerId,
              socialMedia: newPublishingInfo.type,
              socialMediaLink: newPublishingInfo.link,
              subscribers: newPublishingInfo.subscribers || null,
              viewCount: newPublishingInfo.viewCount || null,
            }),
          );
        }
      }

      // Logic for EDITING existing channels
      if (isEditMode) {
        // Note: We create dummy objects for validation to reuse the function
        const editErrors = validateChannelInputs(
          { type: selectedContactChannel?.socialMedia, ...editContactForm },
          { type: selectedSocialMedia?.socialMedia, ...editPublishingForm },
          [],
          [], // Pass empty arrays to skip duplicate checks
        );
        if (Object.keys(editErrors).length > 0)
          throw new Error('Please fix the validation errors.');

        if (editContactForm.id) {
          promises.push(
            updateSocialContact(editContactForm.id, {
              accountIdentifier: editContactForm.identifier,
              socialMediaLink: editContactForm.link,
            }),
          );
        }
        if (editPublishingForm.id) {
          promises.push(
            updateSocialMedia(editPublishingForm.id, {
              socialMediaLink: editPublishingForm.link,
              subscribers: editPublishingForm.subscribers,
              viewCount: editPublishingForm.viewCount,
            }),
          );
        }
      }

      const results = await Promise.all(promises);
      results.forEach((res) => {
        if (!res.status) throw new Error(res.message || 'An error occurred.');
      });

      toast.success('Channel information saved successfully!');

      // Refetch all page data to ensure consistency
      if (lastQuery) {
        console.debug(
          'Refetching data with last query to update UI:',
          lastQuery,
        );
        let freshResults = [];
        if (lastQuery.type === 'single') {
          freshResults = await runSingleQuery(lastQuery.params);
        } else if (lastQuery.type === 'multiple') {
          freshResults = await runMultipleQuery(lastQuery.params);
        }
        // This is the critical step that updates the global state
        setQueryResults(freshResults);
      } else {
        // Fallback if no query is stored, fetch just this influencer's data
        console.warn(
          'No last query found, cannot refetch list. Fetching single influencer data as a fallback.',
        );
        const updatedInfluencer =
          await getInfluencerDetails(activeinfluencerId);
        console.log('UpdatedInfluencer: ', updatedInfluencer);
        if (updatedInfluencer.status) {
          // This is less ideal as it replaces the whole list, but it's a safe fallback.
          setQueryResults([updatedInfluencer.data]);
        }
      }
      // --- REFACTORED PAGE STATE REFRESH ---
      // We now fetch the aggregated payload instead of using old refetch methods
      // preserving health tier, focus tier, and all other metadata accurately.
      const pageDataResult = await fetchInfluencerPageData(activeinfluencerId);
      if (pageDataResult.status) {
        const { influencerInfo, products, socialMedias, socialContacts } =
          pageDataResult.data;
        setInfluencerData(influencerInfo ? [influencerInfo] : []);
        setProductsData(products || []);
        setProductUpdates(products || []);
        setSocialMedias(socialMedias || []);
        setInfluencerContactChannelsData(socialContacts || []);

        const activeContact =
          socialContacts?.find((c) => c.activeContact === 1) ||
          socialContacts?.[0] ||
          null;
        setSelectedContactChannel(activeContact);
      } else {
        console.warn('Could not refresh UI data after update.');
      }

      handleCancel(); // Exit edit/add mode
    } catch (err) {
      console.error('Error saving channels:', err);
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setIsSavingChannel(false);
      setSpinnerMessage('');
    }
  }, [
    isAddMode,
    isEditMode,
    newContactInfo,
    newPublishingInfo,
    editContactForm,
    editPublishingForm,
    activeinfluencerId,
    addSocialContact,
    addSocialMedia,
    updateSocialContact,
    updateSocialMedia,
    fetchInfluencerPageData,
    setError,
    socialMedias,
    influencerContactChannelsData,
    setQueryResults,
    toggleAddMode,
    lastQuery,
    runMultipleQuery,
    runSingleQuery,
    selectedContactChannel,
    selectedSocialMedia,
  ]);

  // --- NEW HANDLERS FOR PARTNER EDIT MODE ---
  const togglePartnerEditMode = useCallback(() => {
    setIsPartnerEditMode((prevIsPartnerEditMode) => {
      if (prevIsPartnerEditMode) {
        // If we are exiting...
        setPartnerValidationErrors({});
      }
      return !prevIsPartnerEditMode;
    });
  }, []);

  const handlePartnerFormChange = (e) => {
    const { name, value } = e.target;
    setPartnerInfoForm((prev) => ({ ...prev, [name]: value }));
    if (partnerValidationErrors[name]) {
      setPartnerValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSavePartnerInfo = useCallback(async () => {
    const errors = validatePartnerInfo(partnerInfoForm);
    setPartnerValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the validation errors before saving partner info.');
      return;
    }

    setIsSavingPartnerInfo(true);
    setSpinnerMessage('Saving partner information...');

    // Prepare payload, converting percent back to decimal
    const payload = {
      ...partnerInfoForm,
      commissionPercent: partnerInfoForm.commissionPercent
        ? parseFloat(partnerInfoForm.commissionPercent) / 100
        : null,
      dateOfPayment: partnerInfoForm.dateOfPayment
        ? new Date(partnerInfoForm.dateOfPayment).toISOString()
        : null,
      revenueUSD:
        partnerInfoForm.revenueUSD !== '' && partnerInfoForm.revenueUSD !== null
          ? parseFloat(partnerInfoForm.revenueUSD)
          : null,
    };

    try {
      const result = await updatePartnerInfo(activeinfluencerId, payload);
      if (!result.status) {
        throw new Error(result.message || 'Failed to save partner info.');
      }
      if (lastQuery) {
        console.debug(
          'Refetching data with last query to update UI:',
          lastQuery,
        );
        let freshResults = [];
        if (lastQuery.type === 'single') {
          freshResults = await runSingleQuery(lastQuery.params);
        } else if (lastQuery.type === 'multiple') {
          freshResults = await runMultipleQuery(lastQuery.params);
        }
        // This is the critical step that updates the global state
        setQueryResults(freshResults);
      }
      // --- SUCCESS, ALSO REFETCH INFLUENCER WHOLE DATA
      const updatedInfluencer = await fetchInfluencerData(activeinfluencerId);
      setInfluencerData(updatedInfluencer || []);
      togglePartnerEditMode(); // Exit edit mode
      console.log('ActiveInfluencerId:', activeinfluencerId);
      console.log('QueryResults:', queryResults);
    } catch (err) {
      console.error('Error saving partner info:', err);
      setError(err.message || 'Could not save partner information.');
    } finally {
      setIsSavingPartnerInfo(false);
      setSpinnerMessage('');
    }
  }, [
    partnerInfoForm,
    activeinfluencerId,
    updatePartnerInfo,
    fetchInfluencerData,
    setError,
    setQueryResults,
    lastQuery,
    runMultipleQuery,
    runSingleQuery,
    queryResults,
    setInfluencerData,
    togglePartnerEditMode,
  ]);

  // --- NEW HANDLERS FOR HEADER EDIT MODE ---
  const toggleHeaderEditMode = useCallback(() => {
    setIsHeaderEditMode((prevIsHeaderEditMode) => {
      if (prevIsHeaderEditMode) {
        // If we are exiting...
        setHeaderValidationErrors({});
      }
      return !prevIsHeaderEditMode;
    });
  }, []);

  const handleHeaderFormChange = (e) => {
    const { name, value } = e.target;
    setHeaderInfoForm((prev) => ({ ...prev, [name]: value }));
    if (headerValidationErrors[name]) {
      setHeaderValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSaveHeaderInfo = useCallback(async () => {
    const errors = validateHeaderInfo(headerInfoForm);
    setHeaderValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the validation errors before saving header info.');
      return;
    }

    setIsSavingHeaderInfo(true);
    setSpinnerMessage('Saving header information...');

    const payload = {
      ...headerInfoForm,
      dateLastContact: headerInfoForm.dateLastContact
        ? new Date(headerInfoForm.dateLastContact).toISOString()
        : null,
    };

    try {
      // We reuse the same endpoint for maximum efficiency
      const result = await updatePartnerInfo(activeinfluencerId, payload);
      if (!result.status) {
        throw new Error(result.message || 'Failed to save header info.');
      }

      // --- REFETCH LOGIC, MIRRORING QueryPage ---
      if (lastQuery) {
        console.debug('Refetching data with last query:', lastQuery);
        let freshResults = [];
        if (lastQuery.type === 'single') {
          freshResults = await runSingleQuery(lastQuery.params);
        } else if (lastQuery.type === 'multiple') {
          freshResults = await runMultipleQuery(lastQuery.params);
        }
        // Update the global state, just like QueryPage does
        setQueryResults(freshResults);
      } else {
        console.warn(
          'No last query found, cannot refetch list. Only fetching single influencer data.',
        );
        // Fallback if there's no stored query (should be rare)
        const updatedInfluencer = await fetchInfluencerData(activeinfluencerId);
        setInfluencerData(updatedInfluencer || []);
      }
      toggleHeaderEditMode(); // Exit edit mode
    } catch (err) {
      console.error('Error saving header info:', err);
      setError(err.message || 'Could not save header information.');
    } finally {
      setIsSavingHeaderInfo(false);
      setSpinnerMessage('');
    }
  }, [
    headerInfoForm,
    activeinfluencerId,
    updatePartnerInfo,
    fetchInfluencerData,
    toggleHeaderEditMode,
    setError,
    lastQuery,
    runMultipleQuery,
    runSingleQuery,
    setQueryResults,
  ]);

  // --- NEW HANDLERS FOR ADD NEW INFLUENCER MODE ---
  const handleEnterAddNewMode = () => {
    // Reset the header form to be completely blank for the new entry
    setHeaderInfoForm({
      name: '',
      status: 'NEW',
      nickname: '',
      country: '',
      idiom: '',
      dateLastContact: '',
    });
    setHeaderValidationErrors({});
    setIsAddingNewInfluencer(true);
    // No need to set header edit mode, as we'll use this new flag to render the form
  };

  const handleCancelAddNew = () => {
    setIsAddingNewInfluencer(false);
  };

  const handleSaveNewInfluencer = useCallback(async () => {
    const errors = validateHeaderInfo(headerInfoForm);
    setHeaderValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the validation errors before saving.');
      return;
    }

    setIsSavingHeaderInfo(true);
    setSpinnerMessage('Creating new influencer...');

    const payload = {
      ...headerInfoForm,
      dateLastContact: headerInfoForm.dateLastContact
        ? new Date(headerInfoForm.dateLastContact).toISOString()
        : null,
    };

    try {
      const result = await addNewInfluencer(payload);
      if (!result?.status || !result.data?.influencerId) {
        throw new Error(result.message || 'Failed to create influencer.');
      }

      const newId = result.data.influencerId;

      // Success! Now, fetch the full data for this new influencer to populate queryResults
      // This requires an API wrapper for your get_influencer_details endpoint
      const newInfluencerDetails = await getInfluencerDetails(newId);

      if (newInfluencerDetails?.status) {
        // Set queryResults to an array with only this new influencer
        setQueryResults([newInfluencerDetails.data]);
        // Set this new influencer as the active one
        setActiveinfluencerId(newId);
        // Activate Query Mode
        setQueryMode(true);
        // Exit the special "add new" mode
        setIsAddingNewInfluencer(false);
      } else {
        throw new Error(
          'Influencer created, but failed to fetch details. Please run a query to find them.',
        );
      }
    } catch (err) {
      console.error('Error saving new influencer:', err);
      setError(err.message);
    } finally {
      setIsSavingHeaderInfo(false);
      setSpinnerMessage('');
    }
  }, [
    headerInfoForm,
    addNewInfluencer,
    getInfluencerDetails,
    setQueryResults,
    setActiveinfluencerId,
    setError,
    setQueryMode,
  ]);

  // --- NEW HANDLERS FOR DELETION ---
  const openDeleteConfirmation = () => setIsDeleteModalOpen(true);
  const closeDeleteConfirmation = () => setIsDeleteModalOpen(false);

  const handleDeleteConfirmed = useCallback(async () => {
    if (!activeinfluencerId) {
      toast.error('No active influencer selected to delete.');
      return;
    }

    setIsDeleting(true);
    try {
      const result = await deleteInfluencer(activeinfluencerId);
      if (!result.status) {
        throw new Error(result.message || 'Failed to delete influencer.');
      }

      toast.success(result.data.message || 'Influencer deleted successfully!');

      // Critical Step: Remove the deleted influencer from the queryResults state
      const updatedQueryResults = queryResults.filter(
        (inf) => inf.influencerId !== activeinfluencerId,
      );
      setQueryResults(updatedQueryResults);

      // If there are other influencers in the list, navigate to the next one (or previous if it was the last one)
      if (updatedQueryResults.length > 0) {
        const originalIndex = queryResults.findIndex(
          (inf) => inf.influencerId === activeinfluencerId,
        );
        // If the deleted one was not the first, go to the one before it. Otherwise, go to the new first one.
        const newIndex = originalIndex > 0 ? originalIndex - 1 : 0;
        setActiveinfluencerId(updatedQueryResults[newIndex].influencerId);
      } else {
        // If it was the last one, reset the view
        setActiveinfluencerId(null);
      }
    } catch (err) {
      console.error('Error deleting influencer:', err);
      toast.error(
        err.message || 'Could not delete influencer. Please try again.',
      );
    } finally {
      setIsDeleting(false);
      closeDeleteConfirmation();
    }
  }, [
    activeinfluencerId,
    deleteInfluencer,
    queryResults,
    setQueryResults,
    setActiveinfluencerId,
  ]);

  // --- NEW HANDLERS FOR HEALTH ANALYSIS ---
  const handleOpenHealthDashboard = useCallback(async () => {
    if (!activeinfluencerId || !selectedSocialMedia) return;

    console.log('selectedSocialMedia: ', selectedSocialMedia);
    // ENHANCEMENT: The check is now against the specific channel selected.
    if (
      selectedSocialMedia?.healthTier === 'Inactive' ||
      selectedSocialMedia?.healthTier === 'Error'
    ) {
      toast.info(
        `This channel's status is ${selectedSocialMedia.healthTier}. Dashboard is not available.`,
      );
      return;
    }

    setIsHealthDashboardLoading(true);
    setIsHealthModalOpen(true);

    const result = await fetchInfluencerHealthDashboard(activeinfluencerId);

    if (result && result.status) {
      setHealthDashboardData(result.data);
      if (lastQuery) {
        console.debug('Refetching data with last query:', lastQuery);
        let freshResults = [];
        if (lastQuery.type === 'single') {
          freshResults = await runSingleQuery(lastQuery.params);
        } else if (lastQuery.type === 'multiple') {
          freshResults = await runMultipleQuery(lastQuery.params);
        }
        // Update the global state, just like QueryPage does
        setQueryResults(freshResults);
      } else {
        console.warn(
          'No last query found, cannot refetch list. Only fetching single influencer data.',
        );
      }
    } else {
      toast.error(result.message || 'Failed to load health dashboard.');
      setIsHealthModalOpen(false); // Close modal on failure
    }
    setIsHealthDashboardLoading(false);
  }, [
    activeinfluencerId,
    selectedSocialMedia,
    activeChannel,
    fetchInfluencerHealthDashboard,
    lastQuery,
    runSingleQuery,
    runMultipleQuery,
    setQueryResults,
  ]);

  const handleRefreshHealth = useCallback(async () => {
    if (!activeinfluencerId || !selectedSocialMedia?.id) return;

    const selectedChannelId = selectedSocialMedia.id; // <-- FIX: Save the current channel ID

    setIsRefreshingHealth(true);
    setSpinnerMessage(
      `Refreshing Channel health analysis for ${activeChannel.nickname || 'the influencer'}...`,
    );

    const result = await refreshInfluencerHealth(
      activeinfluencerId,
      selectedChannelId,
    );

    if (result && result.status) {
      toast.success('Health analysis updated successfully!');

      // After the global state is updated, we now explicitly tell this
      // component to re-fetch its own local data using the same function
      // it uses for the initial page load. This synchronizes the local state
      // with the newly updated database information.
      console.log(
        '[handleRefreshHealth] Refresh successful. Re-running local page data fetch to sync UI.',
      );
      const pageDataResult = await fetchInfluencerPageData(activeinfluencerId);
      if (pageDataResult.status) {
        const { influencerInfo, products, socialMedias, socialContacts } =
          pageDataResult.data;
        setInfluencerData(influencerInfo ? [influencerInfo] : []);
        setProductsData(products || []);
        setProductUpdates(products || []);
        setSocialMedias(socialMedias || []);
        setInfluencerContactChannelsData(socialContacts || []);
        const activeContact =
          socialContacts?.find((c) => c.activeContact === 1) ||
          socialContacts?.[0] ||
          null;
        setSelectedContactChannel(activeContact);
        // <-- FIX: Re-select the correct channel after data refetch
        const reselectedChannel = (socialMedias || []).find(
          (sm) => sm.id === selectedChannelId,
        );
        setSelectedSocialMedia(
          reselectedChannel || (socialMedias ? socialMedias[0] : null),
        );
        // <-- END FIX
      } else {
        setError(
          pageDataResult.message || 'Could not refresh UI data after update.',
        );
      }
    } else {
      toast.error(result.message || 'Failed to refresh health analysis.');
      setError(result.message);
    }
    setSpinnerMessage('');
    setIsRefreshingHealth(false);
  }, [
    activeinfluencerId,
    selectedSocialMedia,
    activeChannel?.nickname,
    refreshInfluencerHealth,
    fetchInfluencerPageData,
    setError,
  ]);

  // --- NEW HANDLERS FOR CONTENT FOCUS ANALYSIS ---
  const handleOpenContentFocusDashboard = useCallback(async () => {
    if (!activeinfluencerId || !selectedSocialMedia) return;

    // ENHANCEMENT: The check is now against the specific channel selected.
    if (
      selectedSocialMedia?.contentFocusTier === 'Insufficient Data' ||
      selectedSocialMedia?.contentFocusTier === 'Error'
    ) {
      toast.info('Content focus analysis is not available for this channel.');
      return;
    }

    setIsContentFocusDashboardLoading(true);
    setIsContentFocusModalOpen(true);

    const result = await fetchInfluencerContentFocusDashboard(
      activeinfluencerId,
      selectedSocialMedia.id,
    );

    if (result && result.status) {
      console.log(
        'Full API Response Data:',
        JSON.stringify(result.data, null, 2),
      );
      setContentFocusDashboardData(result.data);
    } else {
      toast.error(result.message || 'Failed to load content focus dashboard.');
      setIsContentFocusModalOpen(false);
    }
    setIsContentFocusDashboardLoading(false);
  }, [
    activeinfluencerId,
    activeChannel,
    selectedSocialMedia,
    fetchInfluencerContentFocusDashboard,
  ]);

  const handleRefreshContentFocus = useCallback(async () => {
    if (!activeinfluencerId || !selectedSocialMedia?.id) return;

    const selectedChannelId = selectedSocialMedia.id; // <-- FIX: Save the current channel ID

    setIsRefreshingContentFocus(true);
    setSpinnerMessage(
      `Refreshing Channel Content Focus analysis for ${activeChannel.nickname || 'the influencer'}...`,
    );

    const result = await refreshInfluencerContentFocus(
      activeinfluencerId,
      selectedChannelId,
    );

    if (result && result.status) {
      toast.success('Content focus analysis updated successfully!');
      console.log(
        '[handleRefreshContentFocus] Refresh successful. Re-running local page data fetch to sync UI.',
      );
      const pageDataResult = await fetchInfluencerPageData(activeinfluencerId);
      if (pageDataResult.status) {
        const { influencerInfo, products, socialMedias, socialContacts } =
          pageDataResult.data;
        setInfluencerData(influencerInfo ? [influencerInfo] : []);
        setProductsData(products || []);
        setProductUpdates(products || []);
        setSocialMedias(socialMedias || []);
        setInfluencerContactChannelsData(socialContacts || []);
        const activeContact =
          socialContacts?.find((c) => c.activeContact === 1) ||
          socialContacts?.[0] ||
          null;
        setSelectedContactChannel(activeContact);
        // <-- FIX: Re-select the correct channel after data refetch
        const reselectedChannel = (socialMedias || []).find(
          (sm) => sm.id === selectedChannelId,
        );
        setSelectedSocialMedia(
          reselectedChannel || (socialMedias ? socialMedias[0] : null),
        );
        // <-- END FIX
      } else {
        setError(
          pageDataResult.message || 'Could not refresh UI data after update.',
        );
      }
    } else {
      toast.error(
        result.message || 'Failed to refresh content focus analysis.',
      );
      setError(result.message);
    }
    setSpinnerMessage('');
    setIsRefreshingContentFocus(false);
  }, [
    activeinfluencerId,
    selectedSocialMedia,
    activeChannel?.nickname,
    refreshInfluencerContentFocus,
    fetchInfluencerPageData, // <-- Add new dependency
    setError,
  ]);

  // --- MODIFIED RENDER LOGIC for the "No Data" case ---
  // Case 1: We are in "add new influencer" mode. This gets its own full-page render.
  if (isAddingNewInfluencer) {
    return (
      <div className="influencer-page">
        {isSavingHeaderInfo && <Spinner message={spinnerMessage} />}
        {error && <ErrorDisplayModal error={error} clearError={clearError} />}
        {/* Render ONLY the header in a special "add new" state */}
        <div className="header-container edit-mode">
          {/* The same edit mode form we already built */}
          <div className="header-form-container">
            {/* --- ROW 1: TEXT INPUTS --- */}
            <div className="edit-row">
              <div className="header-group">
                <label htmlFor="header-name">Channel Name</label>
                <input
                  id="header-name"
                  type="text"
                  name="name"
                  value={headerInfoForm.name}
                  onChange={handleHeaderFormChange}
                  className={headerValidationErrors.name ? 'invalid' : ''}
                  style={{ fontSize: '1.1rem', fontWeight: 'bold' }}
                />
                {headerValidationErrors.name && (
                  <span className="validation-error-text">
                    {headerValidationErrors.name}
                  </span>
                )}
              </div>
              <div className="header-group">
                <label htmlFor="header-nickname">Nickname</label>
                <input
                  id="header-nickname"
                  type="text"
                  name="nickname"
                  value={headerInfoForm.nickname}
                  onChange={handleHeaderFormChange}
                  className={headerValidationErrors.nickname ? 'invalid' : ''}
                />
                {headerValidationErrors.nickname && (
                  <span className="validation-error-text">
                    {headerValidationErrors.nickname}
                  </span>
                )}
              </div>
              <div className="header-group">
                <label htmlFor="header-idiom">Idiom</label>
                <select
                  id="header-idiom"
                  name="idiom"
                  value={headerInfoForm.idiom}
                  onChange={handleHeaderFormChange}
                  className={headerValidationErrors.idiom ? 'invalid' : ''}
                >
                  <option value="">Select Idiom</option>
                  {languageList.map((language) => (
                    <option key={language.label} value={language.label}>
                      {language.label}
                    </option>
                  ))}
                </select>
                {headerValidationErrors.idiom && (
                  <span className="validation-error-text">
                    {headerValidationErrors.idiom}
                  </span>
                )}
              </div>
            </div>
            {/* --- ROW 2: SELECTS AND BUTTONS --- */}
            <div className="edit-row">
              <div className="header-group">
                <label htmlFor="header-status">Status</label>
                <select
                  id="header-status"
                  name="status"
                  value={headerInfoForm.status}
                  onChange={handleHeaderFormChange}
                  className={headerValidationErrors.status ? 'invalid' : ''}
                >
                  <option value="">Select Status</option>
                  {MENU_STATUS.map(
                    (status) =>
                      status !== 'ALL' && (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ),
                  )}
                </select>
                {headerValidationErrors.status && (
                  <span className="validation-error-text">
                    {headerValidationErrors.status}
                  </span>
                )}
              </div>
              <div className="header-group">
                <label htmlFor="header-country">Country</label>
                <select
                  id="header-country"
                  name="country"
                  value={headerInfoForm.country}
                  onChange={handleHeaderFormChange}
                  className={headerValidationErrors.country ? 'invalid' : ''}
                >
                  <option value="">Select Country</option>
                  {countryList.map((country) => (
                    <option key={country.value} value={country.label}>
                      {country.label}
                    </option>
                  ))}
                </select>
                {headerValidationErrors.country && (
                  <span className="validation-error-text">
                    {headerValidationErrors.country}
                  </span>
                )}
              </div>
              <div className="header-group">
                <label htmlFor="header-last-contact">Last Contact</label>
                <input
                  id="header-last-contact"
                  type="date"
                  name="dateLastContact"
                  value={headerInfoForm.dateLastContact}
                  onChange={handleHeaderFormChange}
                  className={
                    headerValidationErrors.dateLastContact ? 'invalid' : ''
                  }
                />
                {headerValidationErrors.dateLastContact && (
                  <span className="validation-error-text">
                    {headerValidationErrors.dateLastContact}
                  </span>
                )}
              </div>
              <div className="header-controls">
                <button
                  onClick={handleSaveNewInfluencer}
                  className="add-channel-btn save-mode"
                  disabled={isSavingHeaderInfo}
                >
                  Save New Influencer
                </button>
                <button
                  onClick={handleCancelAddNew}
                  className="cancel-add-btn"
                  disabled={isSavingHeaderInfo}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoadingQueryData && !activeInfluencer) {
    return <Spinner message="Loading profile details..." />;
  }

  // 2. If not loading, but still missing (e.g. fetch failed), show error/empty.
  if (!activeInfluencer && activeinfluencerId && !isLoadingQueryData) {
    return (
      <div className="messages-page">
        <p className="noData">
          Could not load data for influencer ID {activeinfluencerId}. Please try
          refreshing the page.
        </p>
      </div>
    );
  }

  // Case 2: No influencer is actively selected. Show a prompt.
  // This is the key fix for the reported issue.
  if (!queryResults || !queryResults.length || !activeinfluencerId) {
    return (
      <div className="influencer-page">
        {error && <ErrorDisplayModal error={error} clearError={clearError} />}
        <p className="noData">
          No influencer selected. Please use the Select Menu to find an
          influencer, or...
        </p>
        <button
          onClick={handleEnterAddNewMode}
          className="actionButton"
          style={{ marginTop: '1rem' }}
        >
          Add New Influencer
        </button>
      </div>
    );
  }

  // If we get here, `activeChannel` is valid and we can render the main page content.
  const currentIndex = queryResults.findIndex(
    (item) => item.influencerId === activeinfluencerId,
  );

  // ROI = Revenue / (Prepaid + (Revenue * Commission%))
  const revenue = parseFloat(influencerInfo.revenueUSD) || 0;
  const prepaid = parseFloat(influencerInfo.publicationPrice) || 0;
  const commissionRate = parseFloat(influencerInfo.commissionPercent) || 0;

  const accruedCommission = revenue * commissionRate;
  const totalInvestment = prepaid + accruedCommission;

  const rawRoi = influencerInfo.roi; // This is the 0.2245 or -0.2593 from your Python code

  const netRoiValue =
    rawRoi !== null && rawRoi !== undefined ? rawRoi * 100 : null;

  const roiClass =
    netRoiValue > 0
      ? 'roi-positive'
      : netRoiValue < 0
        ? 'roi-negative'
        : 'roi-neutral';

  const formattedROI =
    netRoiValue !== null
      ? (netRoiValue > 0 ? '+' : '') + netRoiValue.toFixed(2) + '%'
      : 'N/A';

  // --- NEW: LOGIC TO DISABLE REFRESH BUTTONS ---
  const isRefreshDisabledForPlatform =
    selectedSocialMedia?.socialMedia !== 'youtube';
  const refreshDisabledMessage = isRefreshDisabledForPlatform
    ? `Refresh for ${selectedSocialMedia?.socialMedia} is under development. Please check back later.`
    : '';

  return (
    <div className="influencer-page">
      {(isLoadingPageData ||
        isSavingProduct ||
        isSavingChannel ||
        isSavingPartnerInfo ||
        isSavingHeaderInfo ||
        isRefreshingHealth ||
        isRefreshingContentFocus) && <Spinner message={spinnerMessage} />}
      {error && <ErrorDisplayModal error={error} clearError={clearError} />}

      {/* --- ADD THE CONFIRMATION MODAL --- */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteConfirmation}
        onConfirm={handleDeleteConfirmed}
        title="Confirm Deletion"
        confirmText="Delete"
        isConfirming={isDeleting}
      >
        <p>
          Are you sure you want to permanently delete the influencer "
          <strong>{activeChannel?.name || 'this influencer'}</strong>"?
        </p>
        <p className="modal-warning-text">
          <strong>This action will:</strong>
          <br />
          1. Archive all their social media profiles (YouTube, Instagram, etc.)
          to a discard list to prevent them from being discovered again.
          <br />
          2. Permanently delete all related data from the main database
          (contacts, publications, partner info, etc.).
        </p>
        <p>This cannot be undone.</p>
      </ConfirmationModal>
      {/* NEW: Health Dashboard Modal */}
      <HealthDashboardModal
        isOpen={isHealthModalOpen}
        onClose={() => setIsHealthModalOpen(false)}
        data={healthDashboardData?.current}
        isLoading={isHealthDashboardLoading}
      />
      {/* NEW: Content Focus Modal */}
      {console.log(
        'Value being passed to getTierClassName:',
        contentFocusDashboardData?.analysis?.focusTier,
      )}
      <ContentFocusDashboardModal
        isOpen={isContentFocusModalOpen}
        onClose={() => setIsContentFocusModalOpen(false)}
        data={contentFocusDashboardData?.analysis}
        isLoading={isContentFocusDashboardLoading}
        tierClassName={getTierClassName(
          contentFocusDashboardData?.analysis?.focusTier,
        )}
      />
      {/* Header Container - NOW DYNAMIC */}
      <div
        className={`header-container ${isHeaderEditMode ? 'edit-mode' : ''}`}
      >
        <div className="header-group">
          <img
            src={activeChannel?.thumbnail}
            alt={`${activeChannel?.name} Thumbnail`}
            className="thumbnail"
          />
        </div>

        {isHeaderEditMode ? (
          // --- HEADER EDIT MODE (WITH VALIDATION DISPLAY) ---
          <div className="header-form-container">
            {/* --- ROW 1: TEXT INPUTS --- */}
            <div className="edit-row">
              <div className="header-group">
                <label htmlFor="header-name">Channel Name</label>
                <input
                  id="header-name"
                  type="text"
                  name="name"
                  value={headerInfoForm.name}
                  onChange={handleHeaderFormChange}
                  className={headerValidationErrors.name ? 'invalid' : ''}
                  style={{ fontSize: '1.1rem', fontWeight: 'bold' }}
                />
                {headerValidationErrors.name && (
                  <span className="validation-error-text">
                    {headerValidationErrors.name}
                  </span>
                )}
              </div>
              <div className="header-group">
                <label htmlFor="header-nickname">Nickname</label>
                <input
                  id="header-nickname"
                  type="text"
                  name="nickname"
                  value={headerInfoForm.nickname}
                  onChange={handleHeaderFormChange}
                  className={headerValidationErrors.nickname ? 'invalid' : ''}
                />
                {headerValidationErrors.nickname && (
                  <span className="validation-error-text">
                    {headerValidationErrors.nickname}
                  </span>
                )}
              </div>
              <div className="header-group">
                <label htmlFor="header-idiom">Idiom</label>
                <select
                  id="header-idiom"
                  name="idiom"
                  value={headerInfoForm.idiom}
                  onChange={handleHeaderFormChange}
                  className={headerValidationErrors.idiom ? 'invalid' : ''}
                >
                  <option value="">Select Idiom</option>
                  {languageList.map((language) => (
                    <option key={language.label} value={language.label}>
                      {language.label}
                    </option>
                  ))}
                </select>
                {headerValidationErrors.idiom && (
                  <span className="validation-error-text">
                    {headerValidationErrors.idiom}
                  </span>
                )}
              </div>
            </div>
            {/* --- ROW 2: SELECTS AND BUTTONS --- */}
            <div className="edit-row">
              <div className="header-group">
                <label htmlFor="header-status">Status</label>
                <select
                  id="header-status"
                  name="status"
                  value={headerInfoForm.status}
                  onChange={handleHeaderFormChange}
                  className={headerValidationErrors.status ? 'invalid' : ''}
                >
                  <option value="">Select Status</option>
                  {MENU_STATUS.map(
                    (status) =>
                      status !== 'ALL' && (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ),
                  )}
                </select>
                {headerValidationErrors.status && (
                  <span className="validation-error-text">
                    {headerValidationErrors.status}
                  </span>
                )}
              </div>
              <div className="header-group">
                <label htmlFor="header-country">Country</label>
                <select
                  id="header-country"
                  name="country"
                  value={headerInfoForm.country}
                  onChange={handleHeaderFormChange}
                  className={headerValidationErrors.country ? 'invalid' : ''}
                >
                  <option value="">Select Country</option>
                  {countryList.map((country) => (
                    <option key={country.value} value={country.label}>
                      {country.label}
                    </option>
                  ))}
                </select>
                {headerValidationErrors.country && (
                  <span className="validation-error-text">
                    {headerValidationErrors.country}
                  </span>
                )}
              </div>
              <div className="header-group">
                <label htmlFor="header-last-contact">Last Contact</label>
                <input
                  id="header-last-contact"
                  type="date"
                  name="dateLastContact"
                  value={headerInfoForm.dateLastContact}
                  onChange={handleHeaderFormChange}
                  className={
                    headerValidationErrors.dateLastContact ? 'invalid' : ''
                  }
                />
                {headerValidationErrors.dateLastContact && (
                  <span className="validation-error-text">
                    {headerValidationErrors.dateLastContact}
                  </span>
                )}
              </div>
              <div className="header-controls">
                <button
                  onClick={handleSaveHeaderInfo}
                  className="add-channel-btn save-mode"
                  disabled={isSavingHeaderInfo}
                >
                  Save
                </button>
                <button
                  onClick={toggleHeaderEditMode}
                  className="cancel-add-btn"
                  disabled={isSavingHeaderInfo}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* --- HEADER VIEW MODE (UNCHANGED) --- */}
            <div className="header-group">
              <h2>{activeChannel?.name}</h2>
              <p>
                Status: <span>{activeChannel?.status}</span>
              </p>
            </div>
            <div className="header-group">
              <p>
                Country: <span>{activeChannel?.country || 'N/A'}</span>
              </p>
              <p>
                Idiom: <span>{activeChannel?.idiom || 'N/A'}</span>
              </p>
            </div>
            <div className="header-group">
              <p>
                Nickname: <span>{activeChannel?.nickname || 'N/A'}</span>
              </p>
              <p>
                Last Contact:{' '}
                <span>
                  {new Date(activeChannel?.dateLastContact).toLocaleString([], {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  }) || 'N/A'}
                </span>
              </p>
            </div>
            {/* The main controls are now separate from the data groups */}
            <div>
              <button
                onClick={toggleHeaderEditMode}
                className="header-edit-btn"
              >
                Edit
              </button>
              <button
                onClick={openDeleteConfirmation}
                className="delete-btn"
                disabled={isDeleting}
              >
                Delete Influencer
              </button>
            </div>
            <div className="navigation">
              <button onClick={handlePrevious} disabled={currentIndex === 0}>
                Previous
              </button>
              <span>
                {currentIndex + 1}/{queryResults.length}
              </span>
              <button
                onClick={handleNext}
                disabled={currentIndex === queryResults.length - 1}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      <div className="main-container">
        <div
          className={`left-content ${isAddMode || isEditMode ? 'add-mode' : ''}`}
        >
          <div className="info-header">
            <h3>Influencer Basic Info</h3>
            <div className="button-group">
              {isAddMode || isEditMode ? (
                <>
                  <button
                    onClick={handleSaveChanges}
                    className="add-channel-btn save-mode"
                    disabled={isSavingChannel}
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    className="cancel-add-btn"
                    disabled={isSavingChannel}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={toggleEditMode}
                    className="add-channel-btn"
                    disabled={isLoadingPageData}
                  >
                    Edit
                  </button>
                  <button
                    onClick={toggleAddMode}
                    className="add-channel-btn"
                    disabled={isLoadingPageData}
                  >
                    Add
                  </button>
                </>
              )}
            </div>
          </div>
          {isAddMode && (
            <>
              {/* --- ADD CONTACT CHANNEL FORM --- */}
              <div className="add-channel-section">
                <h4>Add New Contact Channel</h4>
                <div className="input-group">
                  <label htmlFor="contact-type">Channel Type</label>
                  <select
                    id="contact-type"
                    name="type"
                    value={newContactInfo.type}
                    onChange={(e) => handleNewChannelChange(e, 'contact')}
                  >
                    <option>gmail</option>
                    <option>instagram</option>
                    <option>facebook</option>
                    <option>whatsapp</option>
                    <option>telegram</option>
                  </select>
                </div>
                <div className="input-group">
                  <label htmlFor="contact-identifier">
                    Contact / Username / Phone
                  </label>
                  <input
                    type="text"
                    id="contact-identifier"
                    name="identifier"
                    value={newContactInfo.identifier}
                    onChange={(e) => handleNewChannelChange(e, 'contact')}
                    placeholder="e.g., user@gmail.com, @username, +15551234"
                    className={validationErrors.identifier ? 'invalid' : ''}
                  />
                  {validationErrors.identifier && (
                    <span className="validation-error-text">
                      {validationErrors.identifier}
                    </span>
                  )}
                </div>
                <div className="input-group">
                  <label htmlFor="contact-link">Media Link (Optional)</label>
                  <input
                    type="text"
                    id="contact-link"
                    name="link"
                    value={newContactInfo.link}
                    onChange={(e) => handleNewChannelChange(e, 'contact')}
                    placeholder="e.g., https://wa.me/15551234"
                    className={validationErrors.contactLink ? 'invalid' : ''}
                  />
                  {validationErrors.contactLink && (
                    <span className="validation-error-text">
                      {validationErrors.contactLink}
                    </span>
                  )}
                </div>
              </div>
              {/* --- ADD PUBLISHING CHANNEL FORM --- */}
              <div className="add-channel-section">
                <h4>Add New Publishing Channel</h4>
                <div className="input-group">
                  <label htmlFor="publishing-type">Channel Type</label>
                  <select
                    id="publishing-type"
                    name="type"
                    value={newPublishingInfo.type}
                    onChange={(e) => handleNewChannelChange(e, 'publishing')}
                  >
                    <option>youtube</option>
                    <option>instagram</option>
                    <option>facebook</option>
                    <option>tiktok</option>
                    <option>vk</option>
                    <option>blog</option>
                    <option>edu</option>
                    <option>website</option>
                  </select>
                </div>
                <div className="input-group">
                  <label htmlFor="publishing-link">Media Link</label>
                  <input
                    type="text"
                    id="publishing-link"
                    name="link"
                    value={newPublishingInfo.link}
                    onChange={(e) => handleNewChannelChange(e, 'publishing')}
                    placeholder="e.g., https://www.youtube.com/c/ChannelName"
                    className={validationErrors.publishingLink ? 'invalid' : ''}
                  />
                  {validationErrors.publishingLink && (
                    <span className="validation-error-text">
                      {validationErrors.publishingLink}
                    </span>
                  )}
                </div>
                <div className="input-group">
                  <label htmlFor="publishing-subs">Subscribers/Followers</label>
                  <input
                    type="number"
                    id="publishing-subs"
                    name="subscribers"
                    value={newPublishingInfo.subscribers}
                    onChange={(e) => handleFormChange(e, 'newPublishing')}
                    placeholder="e.g., 50000"
                    className={validationErrors.subscribers ? 'invalid' : ''}
                  />
                  {validationErrors.subscribers && (
                    <span className="validation-error-text">
                      {validationErrors.subscribers}
                    </span>
                  )}
                </div>
                <div className="input-group">
                  <label htmlFor="publishing-views">Views/Posts</label>
                  <input
                    type="number"
                    id="publishing-views"
                    name="viewCount"
                    value={newPublishingInfo.viewCount}
                    onChange={(e) => handleFormChange(e, 'newPublishing')}
                    placeholder="e.g., 1500000"
                    className={validationErrors.viewCount ? 'invalid' : ''}
                  />
                  {validationErrors.viewCount && (
                    <span className="validation-error-text">
                      {validationErrors.viewCount}
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
          {isEditMode && (
            <>
              {/* --- EDIT FORMS --- */}
              <div className="add-channel-section">
                <h4>Edit Contact Channel</h4>
                <div className="input-group">
                  <label>Channel</label>
                  <select
                    className="social-media-dropdown"
                    value={selectedContactChannel?.id || ''}
                    onChange={handleContactChannelChange}
                  >
                    {influencerContactChannelsData.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.socialMedia} - {c.accountIdentifier}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedContactChannel && (
                  <>
                    <div className="input-group">
                      <label>Contact / Username</label>
                      <input
                        type="text"
                        name="identifier"
                        value={editContactForm.identifier}
                        onChange={(e) => handleFormChange(e, 'editContact')}
                        className={validationErrors.identifier ? 'invalid' : ''}
                      />
                      {validationErrors.identifier && (
                        <span className="validation-error-text">
                          {validationErrors.identifier}
                        </span>
                      )}
                    </div>
                    <div className="input-group">
                      <label>Media Link (Optional)</label>
                      <input
                        type="text"
                        name="link"
                        value={editContactForm.link}
                        onChange={(e) => handleFormChange(e, 'editContact')}
                        className={
                          validationErrors.contactLink ? 'invalid' : ''
                        }
                      />
                      {validationErrors.contactLink && (
                        <span className="validation-error-text">
                          {validationErrors.contactLink}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="add-channel-section">
                <h4>Edit Publishing Channel</h4>
                <div className="input-group">
                  <label>Channel</label>
                  <select
                    className="social-media-dropdown"
                    value={selectedSocialMedia?.id || ''}
                    onChange={(e) =>
                      setSelectedSocialMedia(
                        socialMedias.find(
                          (sm) => String(sm.id) === e.target.value,
                        ),
                      )
                    }
                  >
                    {socialMedias.map((sm) => (
                      <option key={sm.id} value={sm.id}>
                        {sm.socialMedia}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedSocialMedia && (
                  <>
                    <div className="input-group">
                      <label>Media Link</label>
                      <input
                        type="text"
                        name="link"
                        value={editPublishingForm.link}
                        onChange={(e) => handleFormChange(e, 'editPublishing')}
                        className={
                          validationErrors.publishingLink ? 'invalid' : ''
                        }
                      />
                      {validationErrors.publishingLink && (
                        <span className="validation-error-text">
                          {validationErrors.publishingLink}
                        </span>
                      )}
                    </div>
                    <div className="input-group">
                      <label>Subscribers/Followers</label>
                      <input
                        type="number"
                        name="subscribers"
                        value={editPublishingForm.subscribers}
                        onChange={(e) => handleFormChange(e, 'editPublishing')}
                        className={
                          validationErrors.subscribers ? 'invalid' : ''
                        }
                      />
                      {validationErrors.subscribers && (
                        <span className="validation-error-text">
                          {validationErrors.subscribers}
                        </span>
                      )}
                    </div>
                    <div className="input-group">
                      <label>Views/Posts</label>
                      <input
                        type="number"
                        name="viewCount"
                        value={editPublishingForm.viewCount}
                        onChange={(e) => handleFormChange(e, 'editPublishing')}
                        className={validationErrors.viewCount ? 'invalid' : ''}
                      />
                      {validationErrors.viewCount && (
                        <span className="validation-error-text">
                          {validationErrors.viewCount}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
          {!isAddMode && !isEditMode && (
            <>
              {/* --- VIEW MODE (EXISTING LOGIC) --- */}
              {/* Contact Channel Section */}
              <div
                className="social-media-header"
                style={{ marginTop: '5px', marginBottom: '5px' }}
              >
                <h3 style={{ marginRight: '10px', display: 'inline' }}>
                  Contact Channel:{' '}
                </h3>
                {influencerContactChannelsData.length > 0 ? (
                  <select
                    className="social-media-dropdown"
                    value={selectedContactChannel?.id || ''}
                    onChange={handleContactChannelChange}
                  >
                    {influencerContactChannelsData.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.socialMedia}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p>No contact channels available.</p>
                )}
              </div>
              <p>
                Contact:{' '}
                <span>
                  {selectedContactChannel?.accountIdentifier || 'N/A'}
                </span>
              </p>
              <p>
                Media Link:{' '}
                <a
                  href={formatUrlForHref(
                    selectedContactChannel?.socialMediaLink,
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {selectedContactChannel?.socialMediaLink || 'N/A'}
                </a>
              </p>

              {/* Publishing Channel Section */}
              <div
                className="social-media-header"
                style={{ marginTop: '15px' }}
              >
                <h3 style={{ marginRight: '10px', display: 'inline' }}>
                  Publishing Channel:{' '}
                </h3>
                {socialMedias.length > 0 ? (
                  <select
                    className="social-media-dropdown"
                    value={selectedSocialMedia?.id || ''}
                    onChange={(e) =>
                      setSelectedSocialMedia(
                        socialMedias.find(
                          (sm) => String(sm.id) === e.target.value,
                        ),
                      )
                    }
                  >
                    {socialMedias.map((sm) => (
                      <option key={sm.id} value={sm.id}>
                        {sm.socialMedia}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p> No publishing channels available.</p>
                )}
              </div>
              <p>
                Media Link:{' '}
                <a
                  href={formatUrlForHref(selectedSocialMedia?.socialMediaLink)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {selectedSocialMedia?.socialMediaLink || 'N/A'}
                </a>
              </p>
              <p>
                Subscribers/Followers:{' '}
                <span>
                  {selectedSocialMedia?.subscribers?.toLocaleString() ?? 'N/A'}
                </span>
              </p>
              {/* ENHANCEMENT: Displaying new insightful view metrics */}
              <p>
                Historic Avg Views: <span>{historicAvgViews}</span>
              </p>
              <p>
                Recent Avg Views:{' '}
                <span style={{ fontWeight: 'bold' }}>{recentAvgViews}</span>
              </p>
              <p>
                Size Tier:{' '}
                <span>{influencerInfo.influencerSizeTier || 'N/A'}</span>
              </p>
              {/* --- HEALTH ANALYSIS SECTION (DATA SOURCE CORRECTED) --- */}
              <div className="health-analysis-section">
                <h3>Channel Health:</h3>
                <div
                  className={`health-tier-badge ${getTierClassName(selectedSocialMedia?.healthTier)}`}
                  onClick={handleOpenHealthDashboard}
                  title={'Click to view detailed health dashboard'}
                >
                  {selectedSocialMedia?.healthTier || 'Not Assessed'}
                </div>
                <span className="assessment-date">
                  Last Assessed:{' '}
                  {selectedSocialMedia?.last_health_assessment_at
                    ? new Date(
                        selectedSocialMedia.last_health_assessment_at,
                      ).toLocaleString([], {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })
                    : 'N/A'}
                </span>
                <button
                  onClick={handleRefreshHealth}
                  className="refresh-health-btn"
                  disabled={isRefreshingHealth || isRefreshDisabledForPlatform}
                  title={
                    isRefreshDisabledForPlatform
                      ? refreshDisabledMessage
                      : 'Refresh health analysis'
                  }
                >
                  <FontAwesomeIcon icon={faSync} spin={isRefreshingHealth} />
                  {isRefreshingHealth ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
            </>
          )}
        </div>
        {/* Right Content - NOW DYNAMIC */}
        <div
          className={`right-content ${isPartnerEditMode ? 'edit-mode' : ''}`}
        >
          <div className="info-header">
            <h3>Partner Information</h3>
            <div className="button-group">
              {isPartnerEditMode ? (
                <>
                  <button
                    onClick={handleSavePartnerInfo}
                    className="add-channel-btn save-mode"
                    disabled={isSavingPartnerInfo}
                  >
                    Save
                  </button>
                  <button
                    onClick={togglePartnerEditMode}
                    className="cancel-add-btn"
                    disabled={isSavingPartnerInfo}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={togglePartnerEditMode}
                  className="add-channel-btn"
                >
                  Edit
                </button>
              )}
            </div>
          </div>

          {isPartnerEditMode ? (
            <>
              {/* --- EDIT MODE FORM --- */}
              <div className="input-group">
                <label>Promo Code</label>
                <input
                  type="text"
                  name="promoCode"
                  value={partnerInfoForm.promoCode}
                  onChange={handlePartnerFormChange}
                />
              </div>
              <div className="input-group">
                <label>Email Promo Code</label>
                <input
                  type="email"
                  name="emailPromoCode"
                  value={partnerInfoForm.emailPromoCode}
                  onChange={handlePartnerFormChange}
                  className={
                    partnerValidationErrors.emailPromoCode ? 'invalid' : ''
                  }
                />
                {partnerValidationErrors.emailPromoCode && (
                  <span className="validation-error-text">
                    {partnerValidationErrors.emailPromoCode}
                  </span>
                )}
              </div>
              <div className="input-group">
                <label>Affiliate Link</label>
                <input
                  type="text"
                  name="affiliateLink"
                  value={partnerInfoForm.affiliateLink}
                  onChange={handlePartnerFormChange}
                  className={
                    partnerValidationErrors.affiliateLink ? 'invalid' : ''
                  }
                />
                {partnerValidationErrors.affiliateLink && (
                  <span className="validation-error-text">
                    {partnerValidationErrors.affiliateLink}
                  </span>
                )}
              </div>
              <div className="input-group">
                <label>UTM</label>
                <input
                  type="text"
                  name="affiliateLink2"
                  value={partnerInfoForm.affiliateLink2}
                  onChange={handlePartnerFormChange}
                  className={
                    partnerValidationErrors.affiliateLink2 ? 'invalid' : ''
                  }
                />
                {partnerValidationErrors.affiliateLink2 && (
                  <span className="validation-error-text">
                    {partnerValidationErrors.affiliateLink2}
                  </span>
                )}
              </div>
              <div className="edit-row" style={{ alignItems: 'flex-start' }}>
                <div className="input-group" style={{ flex: 1 }}>
                  <label>Content Prepaid ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="publicationPrice"
                    value={partnerInfoForm.publicationPrice}
                    onChange={handlePartnerFormChange}
                  />
                </div>
                <div className="input-group" style={{ flex: 1 }}>
                  <label>Commission (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    name="commissionPercent"
                    value={partnerInfoForm.commissionPercent}
                    onChange={handlePartnerFormChange}
                  />
                </div>
                <div className="input-group" style={{ flex: 1 }}>
                  <label>Last Payment Date</label>
                  <input
                    type="date"
                    name="dateOfPayment"
                    value={partnerInfoForm.dateOfPayment}
                    onChange={handlePartnerFormChange}
                  />
                </div>
              </div>
              <div className="input-group">
                <label>Revenue (USD)</label>
                <input
                  type="number"
                  step="0.01"
                  name="revenueUSD"
                  value={partnerInfoForm.revenueUSD}
                  onChange={handlePartnerFormChange}
                  className={
                    partnerValidationErrors.revenueUSD ? 'invalid' : ''
                  }
                />
                {partnerValidationErrors.revenueUSD && (
                  <span className="validation-error-text">
                    {partnerValidationErrors.revenueUSD}
                  </span>
                )}
              </div>
            </>
          ) : (
            <>
              {/* --- VIEW MODE (EXISTING LOGIC) --- */}
              <p>
                Promo Code: <span>{influencerInfo.promoCode || 'N/A'}</span>
              </p>
              <p>
                Email Promo Code:{' '}
                <span>{influencerInfo.emailPromoCode || 'N/A'}</span>
              </p>
              <p>
                Affiliate Link:{' '}
                <a
                  href={formatUrlForHref(influencerInfo.affiliateLink)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {influencerInfo.affiliateLink || 'N/A'}
                </a>
              </p>
              <p>
                UTM:{' '}
                <a
                  href={formatUrlForHref(influencerInfo.affiliateLink2)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {influencerInfo.affiliateLink2 || 'N/A'}
                </a>
              </p>
              <div
                style={{
                  margin: '1rem 0',
                  backgroundColor: '#2c3046',
                  padding: '0.8rem',
                  borderRadius: '0.5rem',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '1.5rem',
                    marginBottom: '0.8rem',
                  }}
                >
                  <p style={{ margin: 0 }}>
                    Content Prepaid:{' '}
                    <span>${influencerInfo.publicationPrice || 'N/A'}</span>
                  </p>
                  <p style={{ margin: 0 }}>
                    Commission (%):{' '}
                    <span>
                      {influencerInfo.commissionPercent
                        ? influencerInfo.commissionPercent * 100
                        : 'N/A'}
                    </span>
                  </p>
                  <p style={{ margin: 0 }}>
                    Commission Paid:{' '}
                    <span>
                      $
                      {influencerInfo.commissionPaid?.toLocaleString() ?? 'N/A'}
                    </span>
                  </p>
                </div>
                <div
                  style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}
                >
                  <p style={{ margin: 0 }}>
                    Commission to Pay:{' '}
                    <span>
                      $
                      {influencerInfo.commisssionPayable?.toLocaleString() ??
                        'N/A'}
                    </span>
                  </p>
                  <p style={{ margin: 0 }}>
                    Last Payment date:{' '}
                    <span>
                      {influencerInfo?.dateOfPayment
                        ? new Date(
                            influencerInfo.dateOfPayment,
                          ).toLocaleDateString()
                        : 'N/A'}
                    </span>
                  </p>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '1.5rem',
                  marginTop: '1rem',
                }}
              >
                <p style={{ margin: 0 }}>
                  Revenue (USD):{' '}
                  <span>
                    ${influencerInfo.revenueUSD?.toLocaleString() ?? '0'}
                  </span>
                </p>
                <p style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                  ROI:
                  <span className={`roi-badge ${roiClass}`}>
                    {formattedROI}
                  </span>
                  <span
                    style={{
                      fontSize: '0.75rem',
                      marginLeft: '10px',
                      color: '#888',
                      fontStyle: 'italic',
                    }}
                  >
                    {netRoiValue > 0
                      ? 'Profitable'
                      : netRoiValue < 0
                        ? 'Loss'
                        : ''}
                  </span>
                </p>
              </div>
              {/* --- NEW CONTENT FOCUS ANALYSIS SECTION --- */}
              <div className="focus-analysis-section">
                <h3>Channel Focus:</h3>
                <div
                  className={`health-tier-badge ${getTierClassName(selectedSocialMedia?.contentFocusTier)}`}
                  onClick={handleOpenContentFocusDashboard}
                  title="Click to view detailed content focus dashboard"
                >
                  {selectedSocialMedia?.contentFocusTier || 'Not Assessed'}
                </div>
                <span className="assessment-date">
                  Last Assessed:{' '}
                  {selectedSocialMedia?.last_focus_assessment_at
                    ? new Date(
                        selectedSocialMedia.last_focus_assessment_at,
                      ).toLocaleString([], {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                      })
                    : 'N/A'}
                </span>
                <button
                  onClick={handleRefreshContentFocus}
                  className="refresh-health-btn"
                  disabled={
                    isRefreshingContentFocus || isRefreshDisabledForPlatform
                  }
                  title={
                    isRefreshDisabledForPlatform
                      ? refreshDisabledMessage
                      : 'Refresh content focus analysis'
                  }
                >
                  <FontAwesomeIcon
                    icon={faSync}
                    spin={isRefreshingContentFocus}
                  />
                  {isRefreshingContentFocus ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="plugin-container-wrapper">
        <div className="plugin-container">
          <h3>Granted Products</h3>
          <PluginSelector
            plugins={productsData}
            onPluginToggle={handlePluginToggle}
          />
          <div className="plugin-container-button">
            <button
              onClick={handleSaveProducts}
              disabled={isSavingProduct || isLoadingPageData}
            >
              Save Plug-ins
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfluencerPage;
